import React from 'react'
import MuiTable from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Grid from '@mui/material/Grid'
import Skeleton from '@mui/material/Skeleton'
import ActionButton from '@/components/buttons/ActionButton'
import { DroppableProvidedProps } from '@hello-pangea/dnd'
import { getWindowDimensions } from '@/utils/screenUtils'
import { useTranslation } from 'react-i18next'
import { IAction, THead } from './tableTypes'

interface ITable {
  headerLabels: THead[]
  headerActions?: IAction[]
  children: React.ReactNode
  showExpand?: boolean
  noButtons?: boolean
  isEditing?: boolean
  isAddOpen?: boolean
  isLoading: boolean
  dropProps?: DroppableProvidedProps
  dropRef?: (element?: HTMLElement) => void
  onAddOpen?: () => void
}

const Table: React.FC<ITable> = ({
  headerLabels,
  headerActions,
  children,
  showExpand,
  noButtons,
  isEditing,
  isAddOpen,
  isLoading,
  dropProps,
  dropRef,
  onAddOpen,
}) => {
  const { t } = useTranslation()
  const [windowDimensions, setWindowDimensions] = React.useState(
    getWindowDimensions(),
  )
  React.useEffect(() => {
    const handleResize = () => {
      setWindowDimensions(getWindowDimensions())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])
  const isMobileWidth = windowDimensions.width < 900

  return (
    <Grid
      container
      direction="column"
      justifyContent="center"
      alignItems="center"
      margin={2}
      style={{
        width: isMobileWidth ? 'calc(100% + 60px)' : '100%',
        marginLeft: isMobileWidth ? '-30px' : '0',
        marginRight: isMobileWidth ? '-30px' : '0',
      }}
    >
      <Grid item sx={{ width: 1 }} zeroMinWidth={true}>
        <TableContainer component={Paper} sx={{ width: '100%' }}>
          <MuiTable stickyHeader>
            <TableHead>
              <TableRow>
                {dropRef ? <TableCell /> : ''}
                {showExpand ? <TableCell /> : ''}
                {headerLabels.map((title, index) => (
                  <TableCell align="left" key={index}>
                    {title}
                  </TableCell>
                ))}
                {!noButtons && (
                  <TableCell sx={{ minWidth: '100px' }} align="right">
                    {headerActions &&
                      headerActions.map((action, index) => (
                        <IconButton
                          key={index}
                          size="small"
                          disabled={action.disabled}
                          onClick={() => action.onClick && action.onClick()}
                        >
                          {action.icon}
                        </IconButton>
                      ))}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody ref={dropRef} {...dropProps}>
              {isLoading ? (
                <>
                  {Array.from(Array(10).keys()).map((key) => (
                    <TableRow key={key}>
                      {dropRef ? (
                        <TableCell key={key}>
                          <Skeleton />
                        </TableCell>
                      ) : (
                        ''
                      )}
                      {showExpand ? (
                        <TableCell key={`${key}_second`}>
                          <Skeleton />
                        </TableCell>
                      ) : (
                        ''
                      )}
                      {headerLabels.map((_key, index) => (
                        <TableCell key={index}>
                          <Skeleton />
                        </TableCell>
                      ))}
                      {!noButtons && (
                        <TableCell
                          key={`${key}_third`}
                          sx={{ minWidth: '100px' }}
                        >
                          <Skeleton />
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </>
              ) : (
                children
              )}
            </TableBody>
          </MuiTable>
        </TableContainer>
      </Grid>
      {onAddOpen && (
        <Grid item padding={2}>
          <ActionButton
            label={t('buttons.add')}
            onClick={() => onAddOpen && onAddOpen()}
            disabled={isEditing || isAddOpen}
          />
        </Grid>
      )}
    </Grid>
  )
}

export default Table
